import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "@/hooks/use-toast";
import { SolutionDetails } from '@/types/solution';
import { buildPrompts, extractUrls, detectSolutionType } from '@/utils/promptBuilder';
import { processAIResponse } from '@/utils/jsonProcessor';
import { getAllTags } from '@/services/tagService';

interface UseAIContentGenerationProps {
  onSuccess?: (content: SolutionDetails) => void;
  onError?: () => void;
}

interface ProxyResponse {
  success: boolean;
  choices?: Array<{
    message: {
      content: string;
    };
  }>;
  usage?: any;
  error?: string;
}

export const useAIContentGeneration = ({ onSuccess, onError }: UseAIContentGenerationProps = {}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateContent = async (solutionName: string, generalInfo: string): Promise<boolean> => {
    if (!solutionName) {
      toast({
        title: "Missing Information",
        description: "Please provide a solution name before generating content.",
        variant: "destructive"
      });
      return false;
    }

    setIsGenerating(true);

    try {
      // Fetch existing tags
      const existingTags = await getAllTags(supabase);
      
      // Extract URLs and detect solution type
      const urls = extractUrls(generalInfo);
      const solutionType = detectSolutionType(urls, generalInfo);
      
      // Build prompts client-side
      const { systemPrompt, userPrompt } = await buildPrompts({
        solutionName,
        generalInfo,
        existingTags
      });

      const { data, error } = await supabase.functions.invoke('generate-ai-content', {
        body: {
          messages: [
            {
              role: 'system',
              content: systemPrompt
            },
            {
              role: 'user',
              content: userPrompt
            }
          ]
        }
      });

      // Log the response or error
      if (error) {
        console.error('Edge Function error response:', error);
        throw error;
      }

      console.log('Edge Function success response:', data);

      const response = data as ProxyResponse;

      if (!response.success || !response.choices?.[0]?.message?.content) {
        throw new Error(response.error || 'Failed to generate content');
      }

      // Process AI response on client side
      console.log('Processing AI response...');
      const aiContent = response.choices[0].message.content;
      const processedContent = processAIResponse(aiContent, solutionName);

      // Success message with solution type feedback
      toast({
        title: "Content Generated Successfully",
        description: `Solution analyzed as: ${solutionType}. ${urls.length > 0 ? `Found ${urls.length} URL(s) for research.` : 'No URLs found, using general knowledge.'}`,
      });

      if (onSuccess) {
        onSuccess(processedContent);
      }

      return true;
    } catch (error) {
      console.error('Error generating AI content:', error);
      
      // More specific error handling
      let errorMessage = "AI processing service is temporarily offline. Please try again later.";
      
      if (error?.message?.includes('API key')) {
        errorMessage = "API configuration issue. Please contact support.";
      } else if (error?.message?.includes('rate limit')) {
        errorMessage = "Rate limit exceeded. Please wait a moment and try again.";
      } else if (error?.message?.includes('timeout')) {
        errorMessage = "Request timed out. Please try again with simpler input.";
      }

      toast({
        title: "Generation Failed",
        description: errorMessage,
        variant: "destructive"
      });

      if (onError) {
        onError();
      }

      return false;
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    isGenerating,
    generateContent
  };
};
